/**
 * ExploreScreen — Papers, Speakers, Exhibitors, Attendees
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  Search, BookOpen, Mic, Building2, Users, Sparkles,
  ChevronRight, Loader2, X, Zap,
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { AvatarImage } from '../components/AvatarImage';
import { useApp } from '../context/AppContext';
import { useEventData } from '../context/EventDataContext';
import type { Paper, Profile, Exhibitor, SponsorDirectory, IndustryPartnerDirectory, Committee } from '@conference/shared';
import { useEventModules } from '../hooks/useEventModules';
import {
  getActiveNavigationTab,
  getExploreNavigation,
  type ModuleNavigationTab,
} from '../utils/moduleNavigation';
import SponsorDirectoryPanel from './partners/SponsorDirectoryPanel';
import IndustryPartnerDirectoryPanel from './partners/IndustryPartnerDirectoryPanel';

// ─── Tab config ───────────────────────────────────────────────────────────────

const TAB_ICONS = {
  papers: BookOpen,
  speakers: Mic,
  exhibitors: Building2,
  sponsors: Building2,
  industry_partners: Building2,
  committee: Users,
  attendees: Users,
} as const;

function formatPaperAuthors(authors: string[] | string | null | undefined): string {
  return Array.isArray(authors)
    ? authors.map(author => author.trim()).filter(Boolean).join(', ')
    : authors?.trim() ?? '';
}

function rankPaperSearchResults(papers: Paper[], query: string): Paper[] {
  const term = query.trim().toLowerCase();
  if (!term) return papers;
  const visibleMatches: Paper[] = [];
  const otherMatches: Paper[] = [];
  for (const paper of papers) {
    const visibleMatch = paper.title.toLowerCase().includes(term)
      || formatPaperAuthors(paper.authors).toLowerCase().includes(term);
    (visibleMatch ? visibleMatches : otherMatches).push(paper);
  }
  // Keep abstract/venue hits and the backend's order within each group, while
  // putting results that match the compact card's visible text first.
  return [...visibleMatches, ...otherMatches];
}

// ─── AI Networking Card ───────────────────────────────────────────────────────

type NetworkingState = 'idle' | 'loading' | 'loaded' | 'error';

function AINetworkingCard({ onViewAll, eventId }: { onViewAll: () => void; eventId: string | null }) {
  const navigate = useNavigate();
  const { services: { attendeeService } } = useEventData();
  const [state, setState] = useState<NetworkingState>('idle');
  const [topMatches, setTopMatches] = useState<{ id: string; name: string; company?: string | null; title?: string | null }[]>([]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setState('loading');
    attendeeService.getAll(eventId)
      .then((attendees) => {
        if (cancelled) return;
        setTopMatches(attendees.slice(0, 3).map(a => ({
          id: String((a as any).id),
          name: a.name,
          company: (a as any).company,
          title: (a as any).title,
        })));
        setState('loaded');
      })
      .catch(() => { if (!cancelled) setState('error'); });
    return () => { cancelled = true; };
  }, [eventId]);

  return (
    <div
      className="rounded-xl border overflow-hidden mb-4"
      style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--app-tag-border)' }}>
        <div className="flex items-center gap-2">
          <div className="size-7 rounded-full flex items-center justify-center" style={{ background: 'var(--app-accent-dim)' }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
          </div>
          <div>
            <span className="text-xs" style={{ color: 'var(--app-accent)' }}>AI Networking Assistant</span>
            <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>Based on your interests</p>
          </div>
        </div>
        {state === 'loading' && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--app-accent)' }} />}
      </div>

      <div className="px-4 py-3">
        {state === 'loading' && (
          <div className="flex flex-col gap-2.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="size-8 rounded-full animate-pulse" style={{ background: 'var(--app-accent-dim)' }} />
                <div className="flex-1">
                  <div className="h-2.5 rounded-full animate-pulse mb-1.5" style={{ width: '60%', background: 'var(--app-accent-dim)' }} />
                  <div className="h-2 rounded-full animate-pulse" style={{ width: '40%', background: 'var(--app-accent-dim)' }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {state === 'loaded' && topMatches.length > 0 && (
          <div className="flex flex-col gap-2.5">
            <p className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--app-text-muted)' }}>Attendees to meet</p>
            {topMatches.map((m, i) => (
              <button
                key={m.id}
                onClick={() => navigate(`/attendee/${m.id}`)}
                className="flex items-center justify-between w-full text-left active:opacity-70"
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center size-6 rounded-full text-[10px]" style={{ background: 'var(--app-accent-dim)', color: 'var(--app-accent)' }}>
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-xs" style={{ color: 'var(--app-text)' }}>{m.name}</p>
                    <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                      {[m.title, m.company].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-3 h-3" style={{ color: 'var(--app-text-subtle)' }} />
              </button>
            ))}
          </div>
        )}

        {(state === 'error' || (state === 'loaded' && topMatches.length === 0)) && (
          <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
            Could not load networking suggestions.
          </p>
        )}
      </div>

      <button
        onClick={() => navigate('/report-builder?mode=networking')}
        className="w-full flex items-center justify-between px-4 py-3 border-t transition-colors active:opacity-70"
        style={{ borderColor: 'var(--app-tag-border)' }}
      >
        <span className="text-xs" style={{ color: 'var(--app-accent)' }}>Get full networking recommendations</span>
        <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
      </button>
    </div>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="rounded-xl p-4 border animate-pulse" style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}>
      <div className="h-4 rounded w-3/4 mb-2" style={{ background: 'var(--app-muted)' }} />
      <div className="h-3 rounded w-1/2" style={{ background: 'var(--app-muted)' }} />
    </div>
  );
}

// ─── Main Screen ─────────────────────────────────────────────────────────────

export default function ExploreScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentEventId, aiPreferences } = useApp();
  const { services: { paperService, speakerService, exhibitorService, attendeeService, partnerService, eventContentService } } = useEventData();
  const { modules, isEnabled } = useEventModules();
  const tabs = getExploreNavigation(modules);
  const activeTabEntry = getActiveNavigationTab(location.pathname, tabs);
  const activeTab = activeTabEntry?.id ?? null;
  const tabSignature = tabs.map((tab) => `${tab.id}:${tab.label}`).join('|');
  const searchPlaceholder = tabs.length > 0
    ? `Search ${tabs.map((tab) => tab.label).join(', ')}...`
    : 'No Explore sections available';

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [papers, setPapers] = useState<Paper[]>([]);
  const [speakers, setSpeakers] = useState<Profile[]>([]);
  const [exhibitors, setExhibitors] = useState<Exhibitor[]>([]);
  const [attendees, setAttendees] = useState<Profile[]>([]);
  const [sponsorDirectory, setSponsorDirectory] = useState<SponsorDirectory | null>(null);
  const [industryPartnerDirectory, setIndustryPartnerDirectory] = useState<IndustryPartnerDirectory | null>(null);
  const [committee, setCommittee] = useState<Committee | null>(null);
  const loadRequestSequence = useRef(0);

  useEffect(() => {
    loadRequestSequence.current += 1;
    setLoading(false);
    setPapers([]);
    setSpeakers([]);
    setExhibitors([]);
    setAttendees([]);
    setSponsorDirectory(null);
    setIndustryPartnerDirectory(null);
    setCommittee(null);
  }, [currentEventId, tabSignature]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Load the active collection once, then reload only after search debounces.
  useEffect(() => {
    if (currentEventId && activeTab) loadTabData(activeTab, currentEventId, debouncedSearch);
  }, [activeTab, currentEventId, debouncedSearch, tabSignature]);

  const loadTabData = useCallback(async (tab: string, eventId: string, q: string) => {
    const requestId = ++loadRequestSequence.current;
    setLoading(true);
    try {
      if (tab === 'papers') {
        const query = q.trim();
        const data = query
          ? await paperService.search(eventId, query)
          : await paperService.getAll(eventId);
        if (requestId === loadRequestSequence.current) setPapers(rankPaperSearchResults(data, query));
      } else if (tab === 'speakers') {
        const data = q
          ? await speakerService.search(eventId, q)
          : await speakerService.getAll(eventId);
        if (requestId === loadRequestSequence.current) setSpeakers(data);
      } else if (tab === 'exhibitors') {
        const data = q
          ? await exhibitorService.search(eventId, q)
          : await exhibitorService.getAll(eventId);
        if (requestId === loadRequestSequence.current) setExhibitors(data);
      } else if (tab === 'attendees') {
        const data = q
          ? await attendeeService.search(eventId, q)
          : await attendeeService.getAll(eventId);
        if (requestId === loadRequestSequence.current) setAttendees(data);
      } else if (tab === 'sponsors') {
        const data = await partnerService.getSponsors(eventId);
        if (requestId === loadRequestSequence.current) setSponsorDirectory(data);
      } else if (tab === 'industry_partners') {
        const data = await partnerService.getIndustryPartners(eventId);
        if (requestId === loadRequestSequence.current) setIndustryPartnerDirectory(data);
      } else if (tab === 'committee') {
        const data = await eventContentService.getCommittee(eventId);
        if (requestId === loadRequestSequence.current) setCommittee(data);
      }
    } catch (err) {
      if (requestId === loadRequestSequence.current) console.error('Explore load error', err);
    } finally {
      if (requestId === loadRequestSequence.current) setLoading(false);
    }
  }, []);

  const handleTabChange = (tab: ModuleNavigationTab) => {
    setSearch('');
    setDebouncedSearch('');
    navigate(tab.path);
  };

  const card = {
    backgroundColor: 'var(--app-surface)',
    borderColor: 'var(--app-border)',
    boxShadow: 'var(--app-card-shadow)',
  };

  const renderContent = () => {
    if (!activeTabEntry) {
      return (
        <div className="flex items-center justify-center py-16 text-center">
          <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>
            No Explore sections are available for this event.
          </p>
        </div>
      );
    }

    if (!currentEventId) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-2">
          <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No event selected</p>
          <button className="text-xs underline" style={{ color: 'var(--app-accent)' }} onClick={() => navigate('/event-selector')}>
            Choose an event
          </button>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      );
    }

    switch (activeTab) {
      case 'papers': {
        if (papers.length === 0) return <EmptyState label={activeTabEntry.label} />;
        return (
          <div className="flex flex-col gap-3">
            {papers.map((paper) => {
              const authors = formatPaperAuthors(paper.authors);
              return (
                <button
                  key={paper.id}
                  onClick={() => navigate(`/paper/${paper.id}`)}
                  className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                  style={card}
                >
                  <p className="text-sm leading-snug" style={{ color: 'var(--app-text)' }}>{paper.title}</p>
                  {authors && (
                    <p className="text-xs mt-2" style={{ color: 'var(--app-text-muted)' }}>{authors}</p>
                  )}
                </button>
              );
            })}
          </div>
        );
      }

      case 'speakers': {
        if (speakers.length === 0) return <EmptyState label={activeTabEntry.label} />;
        return (
          <div className="flex flex-col gap-3">
            {speakers.map((speaker) => (
              <button
                key={speaker.id}
                onClick={() => navigate(`/speaker/${speaker.id}`)}
                className="rounded-xl p-4 border flex items-center gap-4 active:scale-[0.98] transition-transform w-full"
                style={card}
              >
                <div className="size-14 rounded-full overflow-hidden flex items-center justify-center shrink-0 text-xl text-white"
                  style={{ background: 'var(--app-accent-dim)' }}>
                  <AvatarImage source={speaker.avatar_url} alt={speaker.name} className="w-full h-full object-cover" fallback={<span style={{ color: 'var(--app-accent)' }}>{speaker.name[0]}</span>} />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm" style={{ color: 'var(--app-text)' }}>{speaker.name}</p>
                  {speaker.title && (
                    <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--app-text-muted)' }}>{speaker.title}</p>
                  )}
                  {speaker.company && (
                    <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-subtle)' }}>{speaker.company}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        );
      }

      case 'exhibitors': {
        if (exhibitors.length === 0) return <EmptyState label={activeTabEntry.label} />;
        return (
          <div className="flex flex-col gap-3">
            {exhibitors.map((ex) => (
              <button
                key={ex.id}
                onClick={() => navigate(`/exhibitor/${ex.id}`)}
                className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                style={card}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm" style={{ color: 'var(--app-text)' }}>{ex.name}</p>
                  {ex.booth && (
                    <span
                      className="text-xs px-2 py-0.5 rounded border"
                      style={{ backgroundColor: 'var(--app-muted)', color: 'var(--app-text-muted)', borderColor: 'var(--app-border)' }}
                    >
                      Booth {ex.booth}
                    </span>
                  )}
                </div>
                {ex.category && (
                  <span
                    className="text-[10px] px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'var(--app-tag-bg)', color: 'var(--app-tag-text)' }}
                  >
                    {ex.category}
                  </span>
                )}
                {ex.description && (
                  <p className="text-xs mt-2 line-clamp-2" style={{ color: 'var(--app-text-muted)' }}>{ex.description}</p>
                )}
              </button>
            ))}
          </div>
        );
      }

      case 'sponsors': {
        if (loading) return <div className="py-4"><SkeletonCard /></div>;
        if (!sponsorDirectory) return <EmptyState label={activeTabEntry.label} />;
        const query = debouncedSearch.trim().toLowerCase();
        const directory = query
          ? { ...sponsorDirectory, sponsors: sponsorDirectory.sponsors.filter((partner) => `${partner.display_name} ${partner.name}`.toLowerCase().includes(query)) }
          : sponsorDirectory;
        return <SponsorDirectoryPanel directory={directory} onOpen={(id) => navigate(`/exhibitor/${id}`)} />;
      }

      case 'industry_partners': {
        if (loading) return <div className="py-4"><SkeletonCard /></div>;
        if (!industryPartnerDirectory) return <EmptyState label={activeTabEntry.label} />;
        const query = debouncedSearch.trim().toLowerCase();
        const directory = query
          ? { ...industryPartnerDirectory, partners: industryPartnerDirectory.partners.filter((partner) => `${partner.display_name} ${partner.name}`.toLowerCase().includes(query)) }
          : industryPartnerDirectory;
        return <IndustryPartnerDirectoryPanel directory={directory} onOpen={(id) => navigate(`/exhibitor/${id}`)} />;
      }

      case 'committee': {
        if (!committee) return <EmptyState label={activeTabEntry.label} />;
        const query = debouncedSearch.trim().toLowerCase();
        const groups = query
          ? committee.groups.flatMap((group) => {
            const members = group.members.filter((member) => (
              `${group.title} ${member.name}`.toLowerCase().includes(query)
            ));
            return members.length > 0 ? [{ ...group, members }] : [];
          })
          : committee.groups;
        if (groups.length === 0) return <EmptyState label={activeTabEntry.label} />;
        return (
          <div className="flex flex-col gap-3">
            {groups.map((group) => (
              <section key={group.id} className="rounded-xl border p-4" style={card}>
                <h2 className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>{group.title}</h2>
                <div className="mt-3 flex flex-col gap-2">
                  {group.members.map((member) => (
                    <p key={member.id} className="text-sm" style={{ color: 'var(--app-text-muted)' }}>{member.name}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        );
      }

      case 'attendees': {
        return (
          <div className="flex flex-col gap-3">
          {isEnabled('ai_copilot') && aiPreferences.aiSuggestions && (
              <AINetworkingCard onViewAll={() => navigate('/report-builder?mode=networking')} eventId={currentEventId} />
            )}
            {attendees.length === 0 ? (
              <EmptyState label={activeTabEntry.label} />
            ) : (
              attendees.map((attendee) => (
                <button
                  key={attendee.id}
                  onClick={() => navigate(`/attendee/${attendee.id}`)}
                  className="rounded-xl p-4 border flex items-center gap-4 active:scale-[0.98] transition-transform w-full"
                  style={card}
                >
                  <div className="size-12 rounded-full overflow-hidden flex items-center justify-center shrink-0 text-lg text-white"
                    style={{ background: 'var(--app-accent-dim)' }}>
                    <AvatarImage source={attendee.avatar_url} alt={attendee.name} className="w-full h-full object-cover" fallback={<span style={{ color: 'var(--app-accent)' }}>{attendee.name[0]}</span>} />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm" style={{ color: 'var(--app-text)' }}>{attendee.name}</p>
                    {attendee.title && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>{attendee.title}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {attendee.company && (
                        <span className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>{attendee.company}</span>
                      )}
                      {attendee.interests?.slice(0, 2).map((interest) => (
                        <span
                          key={interest}
                          className="text-[9px] px-1.5 py-0.5 rounded-full"
                          style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                  {aiPreferences.aiSuggestions && (
                    <Zap className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--app-accent)' }} />
                  )}
                </button>
              ))
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="size-full flex flex-col overflow-hidden transition-colors duration-300" style={{ backgroundColor: 'var(--app-bg)' }}>
      {/* Header */}
      <div className="app-screen-header px-4 pt-12 pb-4 shrink-0" style={{ backgroundColor: 'var(--app-bg)' }}>
        <h1 className="text-2xl mb-4" style={{ color: 'var(--app-text)' }}>Explore</h1>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-10 pr-10 py-3 rounded-lg text-sm outline-none transition-colors"
            style={{
              backgroundColor: 'var(--app-input-bg)',
              border: '1px solid var(--app-input-border)',
              color: 'var(--app-text)',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1" style={{ scrollbarWidth: 'none' }}>
          {tabs.map((tab) => {
            const Icon = TAB_ICONS[tab.id as keyof typeof TAB_ICONS];
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab)}
                className="shrink-0 whitespace-nowrap flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs transition-colors"
                title={tab.label}
                style={
                  active
                    ? { backgroundColor: 'var(--app-accent-solid)', color: '#fff' }
                    : { backgroundColor: 'var(--app-surface)', color: 'var(--app-text-muted)', border: '1px solid var(--app-border)' }
                }
              >
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="app-nav-content flex-1 overflow-y-auto px-4">
        {renderContent()}
      </div>

      <BottomNav />
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-2">
      <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>No {label} found</p>
      <p className="text-xs" style={{ color: 'var(--app-text-subtle)' }}>
        Try adjusting your search or check back later
      </p>
    </div>
  );
}
